import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { BackButton } from "@/components/BackButton";
import { FormField, SelectField } from "@/components/FormField";
import { PrimaryButton } from "@/components/PrimaryButton";

export const Route = createFileRoute("/tools/weight")({
  component: WeightToolPage,
});

const CALIBRATIONS = [
  "Standard (1.78g/card)",
  "Sleeved (2.2g/card)",
  "Double-sleeved (2.6g/card)",
  "Bulk box estimate (1.9g/card)",
];

const CALIBRATION_VALUES: Record<string, number> = {
  "Standard (1.78g/card)": 1.78,
  "Sleeved (2.2g/card)": 2.2,
  "Double-sleeved (2.6g/card)": 2.6,
  "Bulk box estimate (1.9g/card)": 1.9,
};

const UNITS = ["grams", "ounces", "kilograms"];

interface CalcResult {
  cards: number;
  totalCost: number;
  bulk100: number;
  bulk50: number;
  bulk25: number;
}

// NOTE (TNK-46): merge with the Lot Analyzer into a single "Lot Estimator"
// (tabs: Weight / From List) — both feed the "should I buy this lot?" decision.
// See docs/tools-organization-review.md.
function WeightToolPage() {
  const [weight, setWeight] = useState("");
  const [unit, setUnit] = useState("grams");
  const [calibration, setCalibration] = useState("Standard (1.78g/card)");
  const [costPerCard, setCostPerCard] = useState("0.06");
  const [calculated, setCalculated] = useState<CalcResult | null>(null);

  const calculate = () => {
    const w = parseFloat(weight) || 0;
    const weightInGrams =
      unit === "grams" ? w : unit === "ounces" ? w * 28.35 : w * 1000;
    const gramsPerCard = CALIBRATION_VALUES[calibration] || 1.78;
    const cards = Math.floor(weightInGrams / gramsPerCard);
    const totalCost = cards * (parseFloat(costPerCard) || 0.06);
    setCalculated({
      cards,
      totalCost,
      bulk100: Math.floor(cards / 100),
      bulk50: Math.floor(cards / 50),
      bulk25: Math.floor(cards / 25),
    });
  };

  return (
    <section className="flex h-full flex-col">
      <div className="border-b border-border pb-3">
        <BackButton fallback={{ to: "/tools" }} />
      </div>

      <div className="flex-1 space-y-1 overflow-y-auto py-4 pr-2">
        <h1 className="text-xl font-bold text-foreground">
          Bulk Weight Estimator
        </h1>
        <p className="mb-3 text-sm leading-snug text-muted-foreground">
          Estimate card count and cost basis from a stack or box weight
        </p>

        <FormField
          label="Weight"
          value={weight}
          onChangeText={setWeight}
          inputMode="decimal"
          placeholder="e.g. 500"
          required
        />
        <SelectField
          label="Unit"
          value={unit}
          options={UNITS}
          onSelect={setUnit}
        />
        <SelectField
          label="Card Type / Calibration"
          value={calibration}
          options={CALIBRATIONS}
          onSelect={setCalibration}
        />
        <FormField
          label="Cost Per Card ($)"
          value={costPerCard}
          onChangeText={setCostPerCard}
          inputMode="decimal"
          placeholder="0.06"
          suffix="$"
        />

        <PrimaryButton label="Calculate" onClick={calculate} />

        {calculated && (
          <div className="mt-3 rounded-md bg-primary p-4 text-primary-foreground">
            <div className="mb-3 text-[11px] font-medium tracking-wider opacity-60">
              ESTIMATION RESULTS
            </div>
            <div className="grid grid-cols-2 gap-4">
              <ResultItem
                label="Estimated Cards"
                value={calculated.cards.toLocaleString()}
              />
              <ResultItem
                label="Total Cost Basis"
                value={`$${calculated.totalCost.toFixed(2)}`}
              />
              <ResultItem
                label="100-card Lots"
                value={String(calculated.bulk100)}
              />
              <ResultItem
                label="50-card Lots"
                value={String(calculated.bulk50)}
              />
              <ResultItem
                label="25-card Lots"
                value={String(calculated.bulk25)}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function ResultItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-xl font-bold text-secondary">{value}</div>
      <div className="text-[11px] opacity-60">{label}</div>
    </div>
  );
}
